import React from 'react';
import {
  BadgeDollarSign,
  CheckCircle2,
  CircleDollarSign,
  CircleOff,
  Clock3,
  ClipboardList,
  CreditCard,
  ReceiptText,
  Settings2,
  Star,
  UserCheck2,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { KPIData } from '../services/kpiService';

const ACCENT = {
  blue: { iconBg: 'bg-blue-50', iconText: 'text-blue-700', bar: 'bg-blue-600', track: 'bg-blue-100', text: 'text-blue-700' },
  cyan: { iconBg: 'bg-cyan-50', iconText: 'text-cyan-700', bar: 'bg-cyan-600', track: 'bg-cyan-100', text: 'text-cyan-700' },
  violet: { iconBg: 'bg-violet-50', iconText: 'text-violet-700', bar: 'bg-violet-600', track: 'bg-violet-100', text: 'text-violet-700' },
  amber: { iconBg: 'bg-amber-50', iconText: 'text-amber-700', bar: 'bg-amber-500', track: 'bg-amber-100', text: 'text-amber-700' },
  emerald: { iconBg: 'bg-emerald-50', iconText: 'text-emerald-700', bar: 'bg-emerald-600', track: 'bg-emerald-100', text: 'text-emerald-700' },
  red: { iconBg: 'bg-red-50', iconText: 'text-red-700', bar: 'bg-red-500', track: 'bg-red-100', text: 'text-red-700' },
  slate: { iconBg: 'bg-slate-100', iconText: 'text-slate-600', bar: 'bg-slate-400', track: 'bg-slate-100', text: 'text-slate-700' },
} as const;

type AccentKey = keyof typeof ACCENT;

const pct = (part: number, total: number) => (total > 0 ? (part / total) * 100 : 0);

interface StatCardProps {
  icon: LucideIcon;
  accent: AccentKey;
  label: string;
  value: string;
  helper: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, accent, label, value, helper }) => {
  const colors = ACCENT[accent];

  return (
    <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
          <p className="mt-3 truncate text-2xl font-semibold tracking-tight text-slate-950 tabular-nums" title={value}>{value}</p>
          <p className="mt-1 text-xs leading-5 text-slate-500">{helper}</p>
        </div>
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${colors.iconBg} ${colors.iconText}`}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
      </div>
    </article>
  );
};

interface MeterProps {
  accent: AccentKey;
  value: number;
  total: number;
  label: string;
}

const Meter: React.FC<MeterProps> = ({ accent, value, total, label }) => {
  const colors = ACCENT[accent];
  const percentage = Math.min(100, pct(value, total));

  return (
    <div
      className={`h-2 w-full overflow-hidden rounded-full ${colors.track}`}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(percentage)}
    >
      <div className={`h-full rounded-full ${colors.bar}`} style={{ width: `${percentage}%` }} />
    </div>
  );
};

interface MiniStatProps {
  icon: LucideIcon;
  accent: AccentKey;
  label: string;
  value: number;
  share: string;
}

const MiniStat: React.FC<MiniStatProps> = ({ icon: Icon, accent, label, value, share }) => {
  const colors = ACCENT[accent];

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <Icon className={`h-4 w-4 shrink-0 ${colors.iconText}`} aria-hidden="true" />
      </div>
      <p className={`mt-2 text-xl font-semibold tabular-nums ${colors.text}`}>{value.toLocaleString()}</p>
      <p className="mt-1 text-[11px] font-medium text-slate-400">{share}% of total</p>
    </div>
  );
};

interface PeriodCellProps {
  label: string;
  value: number;
  accent: AccentKey;
}

const PeriodCell: React.FC<PeriodCellProps> = ({ label, value, accent }) => {
  const colors = ACCENT[accent];

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-3 text-center">
      <p className="text-[11px] font-medium text-slate-500">{label}</p>
      <p className={`mt-1 text-lg font-semibold tabular-nums ${colors.text}`}>{value.toLocaleString()}</p>
    </div>
  );
};

interface SectionHeaderProps {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ id, eyebrow, title, description }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">{eyebrow}</p>
    <div className="mt-1 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
      <h2 id={id} className="text-lg font-semibold tracking-tight text-slate-950">{title}</h2>
      <p className="text-sm text-slate-500">{description}</p>
    </div>
  </div>
);

interface PanelHeaderProps {
  icon: LucideIcon;
  accent: AccentKey;
  title: string;
  description: string;
  badge?: string;
}

const PanelHeader: React.FC<PanelHeaderProps> = ({ icon: Icon, accent, title, description, badge }) => {
  const colors = ACCENT[accent];

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${colors.iconBg} ${colors.iconText}`}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h3 className="text-base font-semibold text-slate-950">{title}</h3>
          <p className="mt-0.5 text-xs leading-5 text-slate-500">{description}</p>
        </div>
      </div>
      {badge && (
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${colors.iconBg} ${colors.text}`}>{badge}</span>
      )}
    </div>
  );
};

interface DashboardStatsProps {
  kpiData: KPIData;
  formatCurrency: (amount: string | number) => string;
}

export const DashboardStats: React.FC<DashboardStatsProps> = ({ kpiData, formatCurrency }) => {
  const providers = kpiData.userMetrics.serviceProviders;
  const users = kpiData.userMetrics.serviceUsers;
  const revenue = kpiData.revenueAndPaymentMetrics;

  const providerInactive = Math.max(0, providers.total - providers.active);
  const userInactive = Math.max(0, users.total - users.activeUsers);
  const weekRevenue = Number.parseFloat(revenue.revenueByPeriod.thisWeek || '0');
  const monthRevenue = Number.parseFloat(revenue.revenueByPeriod.thisMonth || '0');
  const todayRevenue = Number.parseFloat(revenue.revenueByPeriod.today || '0');
  const totalRevenue = Number.parseFloat(revenue.totalRevenue || '0');
  const subscriptionsTotal = (revenue.subscriptions.active || 0) + (revenue.subscriptions.expired || 0);

  const paymentStatusRows: {
    key: keyof typeof revenue.paymentStatusBreakdown;
    label: string;
    icon: LucideIcon;
    accent: AccentKey;
  }[] = [
    { key: 'completed', label: 'Completed', icon: CheckCircle2, accent: 'emerald' },
    { key: 'pending', label: 'Pending', icon: Clock3, accent: 'amber' },
    { key: 'not_required', label: 'Not required', icon: CircleOff, accent: 'slate' },
    { key: 'expired', label: 'Expired', icon: CircleOff, accent: 'red' },
    { key: 'required_after_first_service', label: 'Required after first service', icon: CreditCard, accent: 'blue' },
  ];
  const paymentStatusTotal = Object.values(revenue.paymentStatusBreakdown).reduce((total, value) => total + (value || 0), 0);

  return (
    <div className="space-y-8">
      <section className="space-y-4" aria-labelledby="platform-snapshot-title">
        <SectionHeader
          id="platform-snapshot-title"
          eyebrow="At a glance"
          title="Platform snapshot"
          description="All-time totals from the latest KPI snapshot"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <StatCard icon={Settings2} accent="blue" label="Service providers" value={kpiData.overview.totalServiceProviders.toLocaleString()} helper="Registered provider accounts" />
          <StatCard icon={UsersRound} accent="cyan" label="Service users" value={kpiData.overview.totalServiceUsers.toLocaleString()} helper="Registered customer accounts" />
          <StatCard icon={ClipboardList} accent="violet" label="Service requests" value={kpiData.overview.totalServiceRequests.toLocaleString()} helper="Requests created to date" />
          <StatCard icon={Star} accent="amber" label="Ratings" value={kpiData.overview.totalRatings.toLocaleString()} helper="Customer ratings recorded" />
          <StatCard icon={CircleDollarSign} accent="emerald" label="Total revenue" value={formatCurrency(kpiData.overview.totalRevenue)} helper="Recognized platform revenue" />
        </div>
      </section>

      <section className="space-y-4" aria-labelledby="audience-health-title">
        <SectionHeader
          id="audience-health-title"
          eyebrow="Audience"
          title="User health"
          description="Activation, verification, and registration momentum"
        />
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <PanelHeader
              icon={Settings2}
              accent="blue"
              title="Service providers"
              description="Provider activation and verification status"
              badge={`${pct(providers.active, providers.total).toFixed(1)}% active`}
            />

            <div className="mt-6 border-b border-slate-100 pb-6">
              <div className="mb-2 flex items-end justify-between gap-4">
                <div>
                  <p className="text-xs font-medium text-slate-500">Registered providers</p>
                  <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-950 tabular-nums">{providers.total.toLocaleString()}</p>
                </div>
                <p className="text-xs font-semibold text-blue-700">{providers.active.toLocaleString()} active</p>
              </div>
              <Meter accent="blue" value={providers.active} total={providers.total} label="Active service providers" />
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <MiniStat icon={CheckCircle2} accent="emerald" label="Active" value={providers.active} share={pct(providers.active, providers.total).toFixed(1)} />
              <MiniStat icon={Clock3} accent="amber" label="Pending verification" value={providers.pendingVerification} share={pct(providers.pendingVerification, providers.total).toFixed(1)} />
              <MiniStat icon={UserCheck2} accent="blue" label="Active users" value={providers.activeUsers} share={pct(providers.activeUsers, providers.total).toFixed(1)} />
              <MiniStat icon={CircleOff} accent="slate" label="Inactive" value={providerInactive} share={pct(providerInactive, providers.total).toFixed(1)} />
            </div>

            <div className="mt-5 border-t border-slate-100 pt-5">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">New registrations</p>
              <div className="grid grid-cols-3 gap-3">
                <PeriodCell label="Today" value={providers.newRegistrations.today} accent="blue" />
                <PeriodCell label="This week" value={providers.newRegistrations.thisWeek} accent="blue" />
                <PeriodCell label="This month" value={providers.newRegistrations.thisMonth} accent="blue" />
              </div>
            </div>
          </article>

          <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <PanelHeader
              icon={UsersRound}
              accent="cyan"
              title="Service users"
              description="Customer activity and registration momentum"
              badge={`${pct(users.activeUsers, users.total).toFixed(1)}% active`}
            />

            <div className="mt-6 border-b border-slate-100 pb-6">
              <div className="mb-2 flex items-end justify-between gap-4">
                <div>
                  <p className="text-xs font-medium text-slate-500">Registered users</p>
                  <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-950 tabular-nums">{users.total.toLocaleString()}</p>
                </div>
                <p className="text-xs font-semibold text-cyan-700">{users.activeUsers.toLocaleString()} active</p>
              </div>
              <Meter accent="cyan" value={users.activeUsers} total={users.total} label="Active service users" />
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <MiniStat icon={UserCheck2} accent="emerald" label="Active users" value={users.activeUsers} share={pct(users.activeUsers, users.total).toFixed(1)} />
              <MiniStat icon={CircleOff} accent="slate" label="Inactive" value={userInactive} share={pct(userInactive, users.total).toFixed(1)} />
            </div>

            <div className="mt-5 border-t border-slate-100 pt-5">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">New registrations</p>
              <div className="grid grid-cols-3 gap-3">
                <PeriodCell label="Today" value={users.newRegistrations.today} accent="cyan" />
                <PeriodCell label="This week" value={users.newRegistrations.thisWeek} accent="cyan" />
                <PeriodCell label="This month" value={users.newRegistrations.thisMonth} accent="cyan" />
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="space-y-4" aria-labelledby="revenue-health-title">
        <SectionHeader
          id="revenue-health-title"
          eyebrow="Commercial performance"
          title="Revenue and membership"
          description="Revenue velocity, subscriptions, and provider payment readiness"
        />
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <PanelHeader icon={BadgeDollarSign} accent="emerald" title="Revenue performance" description="Recognized revenue across reported periods" />

            <div className="mt-6 rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-emerald-700">All-time revenue</p>
              <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-950 tabular-nums sm:text-4xl">{formatCurrency(revenue.totalRevenue)}</p>
            </div>

            <div className="mt-5 space-y-4">
              {[
                { label: 'Today', value: todayRevenue, helper: 'Current day' },
                { label: 'This week', value: weekRevenue, helper: `~${formatCurrency(weekRevenue / 7 || 0)} daily average` },
                { label: 'This month', value: monthRevenue, helper: `~${formatCurrency(monthRevenue / 30 || 0)} daily average` },
              ].map((row) => (
                <div key={row.label}>
                  <div className="mb-2 flex items-end justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-slate-700">{row.label}</p>
                      <p className="mt-0.5 text-xs text-slate-500">{row.helper}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-950 tabular-nums">{formatCurrency(row.value)}</p>
                      <p className="mt-0.5 text-[11px] font-semibold text-emerald-700">{pct(row.value, totalRevenue).toFixed(1)}% of total</p>
                    </div>
                  </div>
                  <Meter accent="emerald" value={row.value} total={totalRevenue} label={`${row.label} share of all-time revenue`} />
                </div>
              ))}
            </div>
          </article>

          <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <PanelHeader icon={ReceiptText} accent="violet" title="Membership and payments" description="Subscription health and provider payment status" />

            <div className="mt-6 border-b border-slate-100 pb-6">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Subscription status</p>
              <div className="grid grid-cols-2 gap-3">
                <MiniStat icon={CheckCircle2} accent="emerald" label="Active" value={revenue.subscriptions.active || 0} share={pct(revenue.subscriptions.active || 0, subscriptionsTotal).toFixed(1)} />
                <MiniStat icon={CircleOff} accent="red" label="Expired" value={revenue.subscriptions.expired || 0} share={pct(revenue.subscriptions.expired || 0, subscriptionsTotal).toFixed(1)} />
              </div>
            </div>

            <div className="mt-5">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Payment status breakdown</p>
              <div className="space-y-4">
                {paymentStatusRows.map(({ key, label, icon: Icon, accent }) => {
                  const value = revenue.paymentStatusBreakdown[key] || 0;
                  const colors = ACCENT[accent];

                  return (
                    <div key={key} className="flex items-center gap-3">
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${colors.iconBg} ${colors.iconText}`}>
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1.5 flex items-center justify-between gap-3">
                          <span className="truncate text-sm text-slate-600">{label}</span>
                          <span className={`text-sm font-semibold tabular-nums ${colors.text}`}>{value.toLocaleString()}</span>
                        </div>
                        <Meter accent={accent} value={value} total={paymentStatusTotal} label={`${label} payment status`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};
